import { useMemo, useState } from 'react';
import { Loader2, Search, Plus, Pencil, MapPin, Power } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import MedicalLabFormModal from '@/components/modals/MedicalLabFormModal';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import {
    useGetMedicalLabsQuery,
    useActivateMedicalLabMutation,
    useDeactivateMedicalLabMutation,
} from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { MedicalLab } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'A';
}

export default function MedicalLabs() {
    const currentUser = useAppSelector(selectCurrentUser);
    // Admin management screen — include deactivated labs.
    const { data: labs = [], isLoading } = useGetMedicalLabsQuery({ includeInactive: true });
    const [activateLab] = useActivateMedicalLabMutation();
    const [deactivateLab] = useDeactivateMedicalLabMutation();

    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<MedicalLab | null>(null);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [error, setError] = useState('');

    const filtered = useMemo(() => {
        if (!search.trim()) return labs;
        const q = search.toLowerCase();
        return labs.filter((lab) =>
            [lab.name, lab.area, lab.city, lab.address, lab.partnerName, lab.region, lab.state]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q)),
        );
    }, [labs, search]);

    const openCreate = () => {
        setEditing(null);
        setModalOpen(true);
    };
    const openEdit = (lab: MedicalLab) => {
        setEditing(lab);
        setModalOpen(true);
    };

    const handleToggle = async (lab: MedicalLab) => {
        setError('');
        setTogglingId(lab.id);
        try {
            if (lab.isActive) {
                await deactivateLab(lab.id).unwrap();
            } else {
                await activateLab(lab.id).unwrap();
            }
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to update the lab.'));
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Admin'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'A'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Medical Labs</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                The catalog CS & medical staff assign from. Deactivate to hide a lab without losing
                                history.
                            </p>
                        </div>
                        <Button
                            onClick={openCreate}
                            className="self-start bg-gray-900 hover:bg-gray-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Add lab
                        </Button>
                    </div>

                    {/* Search */}
                    <div className="relative w-full sm:max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search name, area, city or partner"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                        />
                    </div>

                    {error && (
                        <div className="rounded-xl border border-red-100 bg-red-50 text-red-700 text-sm px-4 py-3">
                            {error}
                        </div>
                    )}

                    {/* Table */}
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-12 px-6">
                                <p className="text-gray-500 text-sm">No labs found.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[760px]">
                                    <thead className="bg-gray-50/60">
                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <th className="px-4 py-3">Lab</th>
                                            <th className="px-4 py-3">Region</th>
                                            <th className="px-4 py-3">Status</th>
                                            <th className="px-4 py-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((lab) => {
                                            const locationLine = [lab.address, lab.city, lab.state]
                                                .filter(Boolean)
                                                .join(', ');
                                            return (
                                                <tr
                                                    key={lab.id}
                                                    className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors"
                                                >
                                                    <td className="px-4 py-4">
                                                        <p className="text-sm font-medium text-gray-900">{lab.name}</p>
                                                        {locationLine && (
                                                            <p className="text-xs text-gray-500 mt-0.5 flex items-start gap-1">
                                                                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
                                                                {locationLine}
                                                            </p>
                                                        )}
                                                        {lab.partnerName && (
                                                            <p className="text-[11px] text-gray-400 mt-0.5">
                                                                Partner: {lab.partnerName}
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-gray-700">
                                                        {lab.region || lab.state || '—'}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <Badge
                                                            variant="outline"
                                                            className={`px-3 py-1 rounded-lg font-medium ${
                                                                lab.isActive
                                                                    ? 'bg-green-50 text-green-700 border-green-100'
                                                                    : 'bg-gray-100 text-gray-500 border-gray-200'
                                                            }`}
                                                        >
                                                            {lab.isActive ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => openEdit(lab)}
                                                                className="border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
                                                            >
                                                                <Pencil className="w-3.5 h-3.5" />
                                                                Edit
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleToggle(lab)}
                                                                disabled={togglingId === lab.id}
                                                                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 ${
                                                                    lab.isActive
                                                                        ? 'border border-red-200 text-red-600 hover:bg-red-50'
                                                                        : 'border border-green-200 text-green-700 hover:bg-green-50'
                                                                }`}
                                                            >
                                                                {togglingId === lab.id ? (
                                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                ) : (
                                                                    <Power className="w-3.5 h-3.5" />
                                                                )}
                                                                {lab.isActive ? 'Deactivate' : 'Activate'}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <MedicalLabFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)} lab={editing} />
        </main>
    );
}
